import {EllipsisOutlined} from '@ant-design/icons';
import {Button, Dropdown, Form, Menu, message, Modal, PageHeader, Space, Table,} from 'antd';
import {useEffect, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {useErrorEffect} from '../../hooks';

const CRUD = ({
                keyName,
                title,
                FormComponent,
                tableColumns,
                apiList,
                apiRetrieve,
                apiCreate,
                apiUpdate,
                apiDelete,
                listRefetchInterval = Infinity,
                searchParams = {},
                itemName = 'datenbankeintrag',
                itemValueName = 'name',
                fieldForChildren = 'id',
                childrenActions,
                childrenActionsBeforeDelete = false,
                commonActions,
                parentId,
                parentName,
                parentField,
                parentFieldParamName,
                modalWidth = 900,
                normalizeFormData = data => data,
                normalizeSendData = data => data,
                extraFields = {},
                enableBulkDelete = false,
                bulkActions = [],
                renderTopContent,
                hidePaginationSizeChanger = false,
                isRowActionDisabled = () => false,
                getRowSelectionCheckboxProps,
              }) => {
  const itemKey = `${keyName}-item`;
  const listKey = `${keyName}-list`;
  const pageSizeStorageKey = `${keyName}-page-size`;
  parentFieldParamName = parentFieldParamName ? parentFieldParamName : parentField;

  const queryClient = useQueryClient();
  const [visibleModal, setVisibleModal] = useState(false);
  const [visibleChildrenModal, setVisibleChildrenModal] = useState(false);
  const [visibleCommonModal, setVisibleCommonModal] = useState(false);
  const [actionKey, setActionKey] = useState(false);
  const [commonActionKey, setCommonActionKey] = useState(false);
  const [recordForChild, setRecordForChild] = useState();
  const [updatedId, setUpdatedId] = useState();
  const [initializedFormId, setInitializedFormId] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const storedPageSize = Number(sessionStorage.getItem(pageSizeStorageKey));
    return [10, 50, 100].includes(storedPageSize) ? storedPageSize : 10;
  });

  const [deleteModal, deleteContextHolder] = Modal.useModal();
  const bulkActionList = Array.isArray(bulkActions) ? bulkActions : [];
  const hasRowSelection = enableBulkDelete || bulkActionList.length > 0;

  const invalidateAfterMutation = async (itemId) => {
    await queryClient.invalidateQueries(listKey);
    if (itemId) {
      await queryClient.invalidateQueries([itemKey, itemId]);
      await queryClient.removeQueries([itemKey, itemId]);
    }
    queryClient.setQueriesData([itemKey], undefined);
  };

  const {mutateAsync: mutateAsyncUpdate, error: errorUpdate} = useMutation(
    apiUpdate, {
      onSuccess: async (data, variables) => await invalidateAfterMutation(variables?.id),
    });
  const {mutateAsync: mutateAsyncCreate, error: errorCreate} = useMutation(
    apiCreate, {
      onSuccess: async () => await invalidateAfterMutation(),
    });
  const {mutateAsync: mutateAsyncDelete} = useMutation(
    apiDelete, {
      onSuccess: () => queryClient.invalidateQueries(listKey),
      onError: (error) => message.error(
        error?.response?.data?.detail || 'Datensatz konnte nicht gelöscht werden.'),
    });

  const [form] = Form.useForm();

  const showUpdate = (record) => {
    form.resetFields();
    setInitializedFormId(undefined);
    setUpdatedId(record.id);
    setVisibleModal(true);
  };

  const getChildrenMenuItems = (record) => childrenActions && Object.keys(childrenActions).map(actionKey => <Menu.Item
    key={actionKey}>
    <a href="#0" onClick={childrenActions[actionKey].onClick ? () => childrenActions[actionKey].onClick(record) : () => showChildren(actionKey, record)}>
      {childrenActions[actionKey].childrenActionName}
    </a>
  </Menu.Item>);

  const getMenu = (record) => (
    <Menu>
      {apiUpdate &&
      <Menu.Item key="update">
        <a href="#0" onClick={() => showUpdate(record)}>Bearbeiten</a>
      </Menu.Item>
      }
      {childrenActionsBeforeDelete && getChildrenMenuItems(record)}
      {apiDelete &&
      <Menu.Item key="delete">
        <a href="#0" onClick={
          () => deleteModal.confirm({
            title: 'Löschen',
            content: `Wirklich löschen ${record[itemValueName]}?`,
            onOk: async () => await mutateAsyncDelete(record.id),
          })
        }>Löschen</a>
      </Menu.Item>
      }
      {!childrenActionsBeforeDelete && getChildrenMenuItems(record)}
    </Menu>
  );

  const columns = [
    ...tableColumns,
    {
      title: 'Aktionen',
      key: 'action',
      width: 100,
      align: 'center',
      render: (text, record) => (
        isRowActionDisabled(record)
          ? null
          : <Dropdown overlay={getMenu(record)} placement="bottomLeft"
                      arrow>
            <Button shape="circle"><EllipsisOutlined/></Button>
          </Dropdown>
      ),
    },
  ];

  const showChildren = async (actionKey, record) => {
    setRecordForChild(record);
    setVisibleChildrenModal(true);
    setActionKey(actionKey);
  };

  const showCommon = async (commonActionKey) => {
    setVisibleCommonModal(true);
    setCommonActionKey(commonActionKey);
  };

  const bulkDeleteHandler = () => {
    const count = selectedRowKeys.length;
    if (!count) {
      return;
    }

    deleteModal.confirm({
      title: 'Ausgewählte löschen',
      content: `Wirklich ${count} Datensätze löschen?`,
      onOk: async () => {
        const results = await Promise.allSettled(
          selectedRowKeys.map((id) => apiDelete(id))
        );
        const deletedCount = results.filter((result) => result.status === 'fulfilled').length;
        const failedCount = results.length - deletedCount;

        if (deletedCount) {
          message.success(`${deletedCount} gelöscht.`);
        }
        if (failedCount) {
          message.error(`${failedCount} konnten nicht gelöscht werden.`);
        }

        setSelectedRowKeys([]);
        await queryClient.invalidateQueries(listKey);
      },
    });
  };

  const bulkActionHandler = (action) => {
    const count = selectedRowKeys.length;
    if (!count) {
      return;
    }

    deleteModal.confirm({
      title: action.confirmTitle || action.buttonLabel,
      content: typeof action.confirmContent === 'function'
        ? action.confirmContent(count, selectedRowKeys)
        : (action.confirmContent || `Wirklich ${count} Datensätze bearbeiten?`),
      onOk: async () => {
        await action.onConfirm(selectedRowKeys);
        setSelectedRowKeys([]);
        await queryClient.invalidateQueries(listKey);
      },
    });
  };

  const handlePageChange = (page, nextPageSize) => {
    if (nextPageSize !== pageSize) {
      handlePageSizeChange(nextPageSize);
      return;
    }
    setCurrentPage(page);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setCurrentPage(1);
    setPageSize(nextPageSize);
    sessionStorage.setItem(pageSizeStorageKey, String(nextPageSize));
  };

  const listParams = {}
  for (const [key, value] of Object.entries(searchParams)) {
    listParams[key] = value;
  }
  if (parentId) {
    listParams[parentFieldParamName] = parentId;
  }

  const {data: {data: listData = []} = {}} = useQuery([listKey, listParams],
    () => {
      return apiList(listParams);
    }, {
      cacheTime: Infinity,
      refetchInterval: listRefetchInterval,
    });

  const {data: {data: itemData = []} = {}} = useQuery([itemKey, updatedId],
    async () => {
      return await apiRetrieve(updatedId);
    }, {
      enabled: !!updatedId,
      cacheTime: Infinity,
    });

  const cancelHandler = async () => {
    form.resetFields();
    setVisibleModal(false);
    setUpdatedId(undefined);
    setInitializedFormId(undefined);
    // await queryClient.invalidateQueries(itemKey);
    // queryClient.setQueriesData([itemKey], undefined);
  };

  const submitHandler = async (values) => {
    const normalValues = normalizeSendData(values);
    if (parentField && parentId) {
      normalValues[parentField] = parentId;
    }
    if (updatedId) {
      await mutateAsyncUpdate({id: updatedId, ...normalValues, ...extraFields});
    } else {
      await mutateAsyncCreate({...normalValues, ...extraFields});
    }
    setVisibleModal(false);
    setUpdatedId(undefined);
    setInitializedFormId(undefined);
    form.resetFields();
  };

  useErrorEffect({form, updatedId, errorUpdate, errorCreate});

  useEffect(() => {
    const hasLoadedItemData = itemData
      && !Array.isArray(itemData)
      && String(itemData.id) === String(updatedId);

    if (hasLoadedItemData && initializedFormId !== updatedId) {
      form.setFieldsValue(normalizeFormData(itemData));
      setInitializedFormId(updatedId);
    }
  }, [itemData, form, initializedFormId, normalizeFormData, updatedId]);

  const ChildrenCRUD = actionKey ? childrenActions[actionKey].ChildrenCRUD : undefined;
  const ActionComponent = commonActionKey ? commonActions[commonActionKey].ActionComponent : undefined;

  return <>
    <Modal
      forceRender
      destroyOnClose={true}
      title={updatedId
        ? `Bearbeiten ${itemName}: ${itemData[itemValueName]}`
        : `Hinzufügen ${itemName}`}
      visible={visibleModal}
      onOk={() => {
        form.submit();
      }}
      onCancel={cancelHandler}
      width={modalWidth}
    >
      <FormComponent
        form={form}
        onFinish={submitHandler}
        itemData={itemData}
        parentFieldParamName={parentFieldParamName}
        parentId={parentId}
        extraFields={extraFields}
      />
    </Modal>

    <Modal
      forceRender
      destroyOnClose={true}
      visible={visibleChildrenModal}
      onCancel={() => setVisibleChildrenModal(false)}
      width={actionKey && childrenActions[actionKey] && childrenActions[actionKey].width ? childrenActions[actionKey].width : '90%'}
      footer=""
    >
      {recordForChild &&
        <ChildrenCRUD parentId={recordForChild[fieldForChildren]}
                      parentName={recordForChild[itemValueName]}
                      setVisible={setVisibleChildrenModal}/>}
    </Modal>

    <Modal
      forceRender
      destroyOnClose={true}
      visible={visibleCommonModal}
      onCancel={() => setVisibleCommonModal(false)}
      width={commonActionKey && commonActions[commonActionKey] && commonActions[commonActionKey].width ? commonActions[commonActionKey].width : '90%'}
      footer=""
    >
      {ActionComponent && <ActionComponent setVisible={setVisibleCommonModal} {...commonActions[commonActionKey]?.extraProps}/>}
    </Modal>

    {deleteContextHolder}

    {title && <PageHeader
      className="site-page-header"
      title={title}
      subTitle={parentName}
    />}

    {renderTopContent && renderTopContent({
      listData,
      currentPage,
      pageSize,
      pageSizeOptions: [10, 50, 100],
      onPageSizeChange: handlePageSizeChange,
    })}

    <Space className="grid-nav">
      {apiCreate && <Button key="add" type="primary" onClick={() => {
        form.resetFields();
        setUpdatedId(undefined);
        setInitializedFormId(undefined);
        setVisibleModal(true);
      }}>
        Hinzufügen
      </Button> }
      {enableBulkDelete && apiDelete && <Button
        danger
        disabled={!selectedRowKeys.length}
        onClick={bulkDeleteHandler}
      >
        Ausgewählte löschen
      </Button>}
      {bulkActionList.map((action) => <Button
        key={action.key}
        type={action.buttonType || 'default'}
        disabled={!selectedRowKeys.length || action.disabled}
        onClick={() => bulkActionHandler(action)}
        {...action.buttonExtraProps}
      >
        {action.buttonLabel}
      </Button>)}
      {commonActions && Object.keys(commonActions).map((slug) => {
        const item = commonActions[slug];
        return <Button key={slug} type={item.buttonType ? item.buttonType : 'primary'}
                       onClick={() => showCommon(slug)} {...item.buttonExtraProps}>
          {item.buttonLabel}
        </Button>
      })}
    </Space>

    <Table
      columns={columns}
      dataSource={listData}
      rowKey="id"
      size="small"
      rowSelection={hasRowSelection ? {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
        getCheckboxProps: (record) => ({
          ...(getRowSelectionCheckboxProps ? getRowSelectionCheckboxProps(record) : {}),
          disabled: isRowActionDisabled(record),
        }),
      } : undefined}
      pagination={{
        current: currentPage,
        pageSize,
        showSizeChanger: !hidePaginationSizeChanger,
        pageSizeOptions: ['10', '50', '100'],
        onChange: handlePageChange,
      }}
      bordered/>
  </>;
};

export default CRUD;
